/* bm_peakExplorer_fullscreenControl.js - fullscreen control for the selection map */
(() => {
  'use strict';

  const FALLBACK_MAP_CLASS = 'bm-custom-map--fullscreenFallback';
  const FALLBACK_BODY_CLASS = 'bm-peakExplorer-fullscreenLock';

  function bm_peakExplorer_attachFullscreenControl({ map, onStatus }) {
    if (!map || typeof map.addControl !== 'function') return null;

    const mapElement = map.getContainer?.();
    if (!mapElement) return null;

    let button = null;
    let container = null;
    let isRemoved = false;
    let isFallbackActive = false;
    let isPending = false;

    const getFullscreenElement = () =>
      document.fullscreenElement || document.webkitFullscreenElement || null;

    const requestNativeFullscreen = () => {
      const request = mapElement.requestFullscreen || mapElement.webkitRequestFullscreen;
      if (typeof request !== 'function') return null;
      return request.call(mapElement);
    };

    const exitNativeFullscreen = () => {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (typeof exit !== 'function') return null;
      return exit.call(document);
    };

    const isActive = () => getFullscreenElement() === mapElement || isFallbackActive;

    const resizeMap = () => {
      window.requestAnimationFrame(() => {
        if (!isRemoved) map.resize?.();
      });
    };

    const syncButton = () => {
      if (!button) return;
      const active = isActive();
      const label = active ? 'Vollbild schließen' : 'Vollbild öffnen';
      button.disabled = isPending;
      button.setAttribute('aria-busy', String(isPending));
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', label);
      button.title = label;
    };

    const activateFallback = () => {
      isFallbackActive = true;
      mapElement.classList.add(FALLBACK_MAP_CLASS);
      document.body.classList.add(FALLBACK_BODY_CLASS);
      syncButton();
      resizeMap();
    };

    const deactivateFallback = () => {
      if (!isFallbackActive) return;
      isFallbackActive = false;
      mapElement.classList.remove(FALLBACK_MAP_CLASS);
      document.body.classList.remove(FALLBACK_BODY_CLASS);
      syncButton();
      resizeMap();
    };

    const openFullscreen = async () => {
      isPending = true;
      syncButton();

      try {
        const fullscreenRequest = requestNativeFullscreen();
        if (fullscreenRequest === null) {
          activateFallback();
        } else {
          await Promise.resolve(fullscreenRequest);
        }
        onStatus?.('Vollbildmodus geöffnet.');
      } catch {
        activateFallback();
        onStatus?.('Vollbildmodus geöffnet.');
      } finally {
        isPending = false;
        syncButton();
        resizeMap();
      }
    };

    const closeFullscreen = async () => {
      isPending = true;
      syncButton();

      try {
        if (isFallbackActive) {
          deactivateFallback();
        } else if (getFullscreenElement() === mapElement) {
          await Promise.resolve(exitNativeFullscreen());
        }
        onStatus?.('Vollbildmodus geschlossen.');
      } catch {
        onStatus?.('Der Vollbildmodus konnte nicht geschlossen werden.', { isError: true });
      } finally {
        isPending = false;
        syncButton();
        resizeMap();
      }
    };

    const handleClick = () => {
      if (isPending) return;
      if (isActive()) {
        void closeFullscreen();
      } else {
        void openFullscreen();
      }
    };

    const handleFullscreenChange = () => {
      syncButton();
      resizeMap();
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isFallbackActive) deactivateFallback();
    };

    const control = {
      onAdd() {
        container = document.createElement('div');
        container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group bm-fullscreen-control';

        button = document.createElement('button');
        button.type = 'button';
        button.className = 'bm-fullscreen-control-button';
        if (mapElement.id) button.setAttribute('aria-controls', mapElement.id);

        const icon = document.createElement('span');
        icon.className = 'bm-fullscreen-control-icon';
        icon.setAttribute('aria-hidden', 'true');
        button.appendChild(icon);
        button.addEventListener('click', handleClick);

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('keydown', handleKeyDown);
        container.appendChild(button);
        syncButton();
        return container;
      },
      onRemove() {
        deactivateFallback();
        button?.removeEventListener('click', handleClick);
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.removeEventListener('keydown', handleKeyDown);
        container?.remove();
        button = null;
        container = null;
        isRemoved = true;
      }
    };

    map.addControl(control, 'top-right');
    if (!button) return null;

    return {
      button,
      isActive,
      destroy() {
        if (isRemoved) return;
        if (getFullscreenElement() === mapElement) {
          Promise.resolve(exitNativeFullscreen()).catch(() => {});
        }
        if (typeof map.removeControl === 'function') map.removeControl(control);
      }
    };
  }

  window.BM = window.BM || {};
  window.BM.bm_peakExplorer_attachFullscreenControl =
    bm_peakExplorer_attachFullscreenControl;
})();
